/**
 * Typeahead Component
 */

import React, { FC, useEffect, useRef, useState } from 'react';
import { withTheme, AnyStyledComponent } from 'styled-components';
import { TypeAheadProps, ListProps, Suggestion, SuggestionsGroup } from './typeAhead.types';
import { styledTypeAheadWrapper, styledListWrapper, styledListItem } from './typeAhead.style';
import { TextInputTypes } from '../TextInput/textInput.types';
import TextInput  from '../TextInput/textInput';
import Handler from '../../commonTypes/handler.types';
import bindHandlers from '../../utils/handlerHelper';
const TypeAheadWrapper: AnyStyledComponent = styledTypeAheadWrapper;
const ListWrapper: AnyStyledComponent = styledListWrapper;
const ListItem: AnyStyledComponent = styledListItem;
const hasClickEvent:boolean = (typeof window !== 'undefined' && typeof window.onclick !== 'undefined');

const TypeAhead: FC<TypeAheadProps> = ({
    placeHolder,
    className,
    type = TextInputTypes.PRIMARY,
    theme = {},
    ariaLabel,
    ariaLabelReset,
    handlers = {},
    resetHandlers,
    value = '',
    cancelButton = true,
    required = false,
    iconType,
    iconColor,
    data = [],
    refElement,
    cancelButtonRef,
    additionalProps = {},
    onItemClick = () => {},
    spellCheck = false,
    shouldClearValueOnSelect = true, //@TODO: Change default value to false on next major release
    inputType = 'text',
  }: TypeAheadProps) => {
    const defaultCurrentResult: number = -1;
    const wrapperRef:any = useRef<HTMLDivElement>(null);
    const [ currentResult, setCurrentResult ] = useState(defaultCurrentResult);
    const inputElRef: any = refElement || useRef<HTMLInputElement>(null);
    const [ inputVisible, setInputVisible ] = useState(false);
    const [valueState, setValueState] = useState(value);
    const [selectedValue, setSelectedValue] = useState('');
    const dataRef = useRef(data);
    const { onClick, ...otherHandlers } = resetHandlers;
    const internalResetHandlers: Handler = {
      onClick: (e: MouseEvent | TouchEvent, shouldResetValue = true) => {
        if(shouldResetValue) {
          setValueState('');
        }
        setSelectedValue('');
        setCurrentResult(defaultCurrentResult);
        if (onClick) {
          onClick(e, shouldResetValue);
        }
      }
    };
    const combinedResetHandlers: Handler = bindHandlers(
      null,
      otherHandlers,
      internalResetHandlers,
    );

    const handleClick = (e: MouseEvent | TouchEvent): void => {
      const currentWrapper:any = wrapperRef.current;
      const isOpen:boolean =  dataRef.current.length > 0;
      const onResetClick = internalResetHandlers && internalResetHandlers.onClick;
      if(!currentWrapper!.contains(e.target) && isOpen && onResetClick) {
        onResetClick(e, false);
        e.stopImmediatePropagation();
      }
    };

    const handleOnItemClick: Function =(e: Event): void => {
      const target = e.currentTarget as HTMLUListElement;
      const index = parseInt(target.getAttribute('data-item-index') as string);

      if (target) {
        const selectedItemIndex = totalDataEntriesMap[index].type === 'data' ? index : index + 1;
        setValueState(shouldClearValueOnSelect ? '' : getOptionText(selectedItemIndex));
        setSelectedValue('');
        setCurrentResult(defaultCurrentResult);
        inputElRef.current.focus();
        onItemClick(e, totalDataEntriesMap[selectedItemIndex]);
      }
    };

    const getOptionText: Function = (index: number) => {
      const { dataMap } = totalDataEntriesMap[index];
      const selectedData: Suggestion | SuggestionsGroup = data[dataMap[0]];
      let optionText = '';
      // check if it is a suggestion or a suggestionsGroup
      if ('text' in selectedData) {
        optionText = selectedData.text;
      } else {
        optionText = selectedData.data[dataMap[1]].text;
      }

      return optionText.trim();
    };

    const handleKeyDown: Function = (e: KeyboardEvent) => {
      const keyCode = e && e.keyCode;
      const down = keyCode === 40;
      const up = keyCode === 38;
      const esc = keyCode === 27;
      const enter = keyCode === 13;

      if (down && currentResult < totalDataEntries - 1) {
        const stepdown  = (totalDataEntriesMap[currentResult + 1].type === 'data') ? 1 : 2;
        setCurrentResult(currentResult + stepdown);
      }

      if (up && currentResult > defaultCurrentResult) {
        e.preventDefault();
        const stepup = currentResult === 0
          ? 1
          : (totalDataEntriesMap[currentResult - 1].type === 'data')
          ? 1
          : 2;
        setCurrentResult(currentResult - stepup);
      }

      if (enter && currentResult !== defaultCurrentResult) {
        e.preventDefault();
        setInputVisible(false);
        inputElRef.current.blur();
        inputElRef.current.focus();
        const selectedItemIndex = totalDataEntriesMap[currentResult].type === 'data' ? currentResult : currentResult + 1;
        setValueState(shouldClearValueOnSelect ? '' : getOptionText(selectedItemIndex));
        setSelectedValue('');
        setCurrentResult(defaultCurrentResult);
        onItemClick(e, totalDataEntriesMap[selectedItemIndex]);
      }

      if (esc) {
        setInputVisible(false);
        internalResetHandlers.onClick(e, false);
      }

  };

  const inputFocus: Function = () => {
      if (!inputVisible) {
          setInputVisible(true);
      }
    };


    useEffect(() => {
      if (hasClickEvent) {
        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
      } else {
        return undefined;
      }
    }, []);

    useEffect(() => {
      if (inputVisible) {
          inputElRef.current.focus();
      }
    }, [inputVisible]);

    useEffect(() => {
      if (currentResult !== defaultCurrentResult) {
        const optionLabel = getOptionText(currentResult);
        setSelectedValue(optionLabel);
      }
    }, [currentResult]);

    useEffect(() => {
      setValueState(value);
      setSelectedValue('');
      setCurrentResult(defaultCurrentResult);
    }, [value]);

    useEffect(() => {
      dataRef.current = data;
    }, [data]);

    let appOnClickHandler: Function;
    let onChangeHandler: Function;
    let appOnFocusHandler: Function;
    let onFocusHandler: Function;
    const themeType: string = theme.dark ? 'dark' : type;

    if (handlers && handlers.onChange) {
      appOnClickHandler = handlers.onChange;
      onChangeHandler = (e: React.SyntheticEvent<EventTarget>) => {
        appOnClickHandler(e);
        setCurrentResult(defaultCurrentResult);
        setValueState(inputElRef.current.value);
        setSelectedValue('');
      };
      handlers.onChange = onChangeHandler;
    }

    if (handlers && !handlers.onFocus) {
      handlers.onFocus = inputFocus;
    } else {
      appOnFocusHandler = handlers.onFocus;
      onFocusHandler = (e: React.SyntheticEvent<EventTarget>) => {
        inputFocus(e);
        appOnFocusHandler(e);
      };
      handlers.onFocus = onFocusHandler;
    }

    handlers.onKeyDown = handleKeyDown;

    const completeTextInputProps: TypeAheadProps = {
      inputType,
      placeHolder,
      ariaLabel,
      refElement: inputElRef,
      required,
      value: (currentResult === defaultCurrentResult) || !selectedValue ? valueState : selectedValue,
      cancelButton,
      iconType,
      iconColor,
      ariaLabelReset,
      theme,
      type,
      handlers,
      resetHandlers: combinedResetHandlers,
      onItemClick,
      additionalProps,
      cancelButtonRef,
      spellCheck,
  };

  const ListWrapperProps: ListProps = {
      theme: themeType
  }
  const listItems:any = [];
  const totalDataEntriesMap: object = {};
  let totalDataEntries: number = 0;
  let totalDataOnlyEntries: number = 0;

  data.forEach((item: Suggestion | SuggestionsGroup, i: number) => {
    // check if data is a single selection
    // otherwise is a group o suggestions
    if ('text' in item) {
      const { text, component, className: liClassName = '' } = item;
      listItems.push(
          <ListItem
            data-item-index={i}
            className={`${(currentResult === i) ? 'active': ''}${liClassName !== '' ? ' ' + liClassName : ''}`}
            key={`typeahead-list-item-${i}`}
            onClick={handleOnItemClick as any}
            {...ListWrapperProps}
            >{component || text}
          </ListItem>
          );
      totalDataEntriesMap[totalDataEntries] = {
        type: 'data',
        dataMap: [i],
        index: i,
      };
      totalDataEntries = i + 1;

    } else {

      Object.keys(item).forEach((key: string) => {
          if (key === 'label') {
            const { className: liClassName = '' } = item;
            listItems.push(
              <ListItem
                  data-item-index={totalDataEntries}
                  data-type={key}
                  key={`typeahead-list-item-label-${i}-${key}`}
                  className={`${liClassName}`}
                  {...ListWrapperProps}
                >
                {item.label}
              </ListItem>
            );
            totalDataEntriesMap[totalDataEntries] = { type: 'label' };
            totalDataEntries = totalDataEntries + 1;

          } else if (key === 'data' && Array.isArray(item.data) ) {

            const dataArr:Array<Suggestion> = item.data;
            dataArr.forEach((dt: Suggestion, idx: number) => {
                const { text, component, className: liClassName } = dt;
                listItems.push(
                  <ListItem
                    data-item-index={totalDataEntries}
                    className={`${(currentResult === totalDataEntries) ? 'active': ''} ${liClassName}`}
                    key={`typeahead-list-item-${listItems.length-1}-${key}`}
                    onClick={handleOnItemClick as any}
                    {...ListWrapperProps}
                  >
                      {component || text}
                  </ListItem>
                );

                totalDataEntriesMap[totalDataEntries] = {
                  type: 'data',
                  dataMap: [i, idx],
                  index: totalDataOnlyEntries,
                };
                totalDataEntries = totalDataEntries + 1;
                totalDataOnlyEntries = totalDataOnlyEntries + 1;
            });
          }
      });
    }
  });

  return (
        <TypeAheadWrapper ref={wrapperRef} className={className}>
            <TextInput { ...completeTextInputProps } />
            {listItems && listItems.length > 0 ?
            <ListWrapper {...ListWrapperProps}>
              {listItems}
            </ListWrapper> : null}
        </TypeAheadWrapper>
    );
  };

export default withTheme(TypeAhead);
